'use client'
import BreadCrumb from "@/components/BreadCrumb";
import { noto_sans, ubuntu, roboto } from "@/globalConstants";
import { Festival, FestivalsOrObservancesData, PanchangaDetails, Tithi } from "@/globalTypes";
import { getFormattedDate } from "@/lib/globalHooks";
import { formatTimeIn } from "@/lib/zonedTime";
import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import { usePathname } from "next/navigation";
import { useRouter } from "next/navigation";
import { Fragment, useState, useEffect, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { useFestivalContext } from "@/contexts/FestivalContext";
import { getAFestivalBySlug, getPanchangaForToday } from "@/services/app.services";
import PrarthanaForFestival from "./PrarthanaForFestival";
import PageBanner from "../PageBanner";
import FestivalFAQ from "./FestivalFAQ";
import { FestivalAnswer, FestivalFaq } from "@/lib/festivalMeta";
import FestivalAppCTA from "./FestivalAppCTA";
import { festivalSlug } from "@/lib/globalUtils";
import { languageSwitch } from "@/lib/festivalTextHi";

// Festival dates from the backend are unix-seconds at IST midnight. Format them
// in a FIXED timezone (IST) so the server render and the client's first render
// are byte-identical (no hydration mismatch) and crawlers see the correct civil
// date regardless of the server's own timezone.
const IST_TZ = 'Asia/Kolkata';

const ordinalSuffix = (day: number) => {
    const lastTwo = day % 100;
    if (lastTwo > 10 && lastTwo < 14) return 'th';
    return ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] || 'th';
};

// e.g. "9th Nov 2026" (default) or "9th November 2026" (longMonth).
const formatFestivalDate = (unixSeconds: number, longMonth = false) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: IST_TZ,
        day: 'numeric',
        month: longMonth ? 'long' : 'short',
        year: 'numeric',
    }).formatToParts(new Date(unixSeconds * 1000));
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    const day = Number(get('day'));
    return `${day}${ordinalSuffix(day)} ${get('month')} ${get('year')}`;
};

const festivalWeekday = (unixSeconds: number) =>
    new Intl.DateTimeFormat('en-US', { timeZone: IST_TZ, weekday: 'long' }).format(new Date(unixSeconds * 1000));

const festivalYear = (unixSeconds: number) =>
    Number(new Intl.DateTimeFormat('en-GB', { timeZone: IST_TZ, year: 'numeric' }).format(new Date(unixSeconds * 1000)));

// `initialTithis`: the festival day's tithis, fetched on the server, so the
// Panchanga box has content in the HTML before the browser fetches its own.
export default function FestivalView({ initialFestival, faqs = [], answer, initialTithis = [], aliases = [], heading, ctaHeading, hindiUrl, cityNote, afterAnswer, beforeFaq, content, contentLinks = [] }: { initialFestival?: { data: FestivalsOrObservancesData | null } | null, faqs?: FestivalFaq[], answer?: FestivalAnswer, initialTithis?: Tithi[], aliases?: string[], heading?: string, ctaHeading?: string, hindiUrl?: string, cityNote?: string, afterAnswer?: ReactNode, beforeFaq?: ReactNode, content?: ReactNode, contentLinks?: { id: string, label: string }[] }) {
    const router = useRouter();
    const BASE_CLASS = 'festivals';
    const pathname = usePathname();
    const [todaysPanchanga, setTodaysPanchanga] = useState<PanchangaDetails>()
    const [showStickyNudge, setShowStickyNudge] = useState(true);
    let { selectedFestival: ctxFestival, loader: ctxLoader, setSelectedFestival, setLoader, fetchUserLocation, pendingSlug } = useFestivalContext();
    const slugFromUrl = pathname.split("/")[2]?.toLowerCase().replaceAll(' ', '-').replaceAll('%20', '-');
    // The festival the context holds, as a URL slug. Uses the API slug like the
    // listing links do: deriving it from the name alone ("Sankashti Chaturthi"
    // vs /festivals/sankasta-chaturthi) made a correct context look stale.
    const contextSlug = ctxFestival?.data?.name ? festivalSlug(ctxFestival.data) : '';
    // A festival picked from the listing whose dated details are still loading.
    const awaitingSelection = Boolean(slugFromUrl) && pendingSlug === slugFromUrl;

    function handleDateChange(newDate: any) {
        router.push('/panchanga/' + dayjs(newDate).format('DD-MM-YYYY'));
    }


    useEffect(() => {
        if (!slugFromUrl) return;
        // Picked from the listing: the context is already loading that date.
        if (awaitingSelection) return;

        // Stale or empty context (a direct visit, or a link from another
        // festival page while FestivalProvider stays mounted in the layout).
        if (ctxFestival.date === 0 || contextSlug !== slugFromUrl) {
            setLoader(true);
            (async () => {
                let slugFestival = await getAFestivalBySlug(slugFromUrl);
                let todaysPanchanga = await getPanchangaForToday(new Date().getTime(), JSON.parse(localStorage.getItem('userLocation') || JSON.stringify(await fetchUserLocation())));
                // The slug endpoint returns `{ data: null }` for unknown slugs.
                // Don't drop the loader in that case — the render path assumes
                // selectedFestival.data is populated whenever loader is false.
                if (slugFestival?.data) {
                    // Undated: the page shows the festival's next occurrence.
                    setSelectedFestival({ ...slugFestival, date: 0 });
                    setTodaysPanchanga(todaysPanchanga.data);
                    setLoader(false);
                } else {
                    console.error('Festival not found for slug:', slugFromUrl);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    // Hide the sticky "Get the App" nudge once the in-page CTA section is visible.
    useEffect(() => {
        const handleScroll = () => {
            const cta = document.getElementById('festival-app-cta');
            if (!cta) return;
            const rect = cta.getBoundingClientRect();
            const triggerPoint = window.innerHeight - rect.height * 0.4;
            setShowStickyNudge(rect.top > triggerPoint);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    // Server-provided initial state: seed the primary content from the festival
    // fetched in page.tsx so the FIRST render (server + client hydration) shows
    // the name, description, story, banner and dated instance — no Skeleton.
    const seededFestival: Festival | null = initialFestival?.data
        ? { date: initialFestival.data.upcoming_dates?.[0] ?? 0, data: initialFestival.data, panchanga: undefined as any }
        : null;

    // Once the client effect has loaded the context (with location-based
    // panchanga), prefer it; until then render the server-seeded festival. These
    // shadow the raw context values so the rest of the render is unchanged.
    // Only when it is this page's festival: a failed lookup used to leave the
    // previous festival in the context and render it under this URL.
    const hasContextData = !ctxLoader && Boolean(ctxFestival?.data?.name) && contextSlug === slugFromUrl;
    const selectedFestival: Festival = hasContextData ? ctxFestival : (seededFestival ?? ctxFestival);
    // "loader" now means: primary content is not yet available for rendering.
    // While a listing pick loads, the server seed would show the next occurrence
    // for a moment, so keep the skeleton up until the picked date arrives.
    const loader = awaitingSelection || !selectedFestival?.data?.name;

    const festivalName = selectedFestival.data?.name || selectedFestival.data?.observance_name || '';
    // The dated instance shown in the banner title/meta and stated in the body.
    // Not every observance has upcoming_dates — eclipses (surya-grahana) and some
    // ekadashis come back with `upcoming_dates: null`. Indexing `[0]` on that threw
    // and 500'd the page, so every read below goes through this optional value and
    // the banner falls back to the undated title.
    const festivalDate: number | undefined = selectedFestival.date || selectedFestival.data?.upcoming_dates?.[0];
    // Reuses the IST-aware festivalYear() helper so the banner year matches the
    // date stated in the body (parity rule), rather than the server's timezone.
    const bannerYear = festivalDate ? festivalYear(festivalDate) : undefined;
    // The server's answer block describes the next occurrence; it's shown only
    // while that is the date on screen (a visitor can pick another occurrence).
    const shownAnswer = !loader && answer && answer.date === festivalDate ? answer : undefined;
    const hasPrarthanas = Boolean(selectedFestival.data?.prarthanas?.length);
    const pageLinks = [
        ...contentLinks.map((link) => ({ href: `#${link.id}`, label: link.label })),
        ...(hasPrarthanas ? [{ href: '#festival-prarthanas', label: 'prarthanas and mantras with audio' }] : []),
        ...(faqs.length > 0 ? [{ href: '#festival-faq', label: `questions about ${festivalName}` }] : []),
    ];

    return (
        <>
        <section className={`${BASE_CLASS}  ${noto_sans.className}`}>
            <PageBanner
                breadcrumb={<BreadCrumb pathname={pathname} />}
                title={loader ? <Skeleton variant='text' width={'160px'} height={'2.5rem'} /> : (heading && answer?.date === festivalDate ? heading : `${festivalName}${bannerYear ? ` ${bannerYear}` : ''}`)}
                subtitle={hindiUrl && !loader ? (
                    <>
                        {aliases.length > 0 && <span>{`Also called ${aliases.join(', ')}`}</span>}
                        <Link href={hindiUrl} hrefLang="hi-IN" className="block text-[1rem] underline">{languageSwitch.toHindi}</Link>
                    </>
                ) : (!loader && aliases.length > 0 ? `Also called ${aliases.join(', ')}` : undefined)}
                meta={
                    <div className="flex items-center gap-2">
                        <Image src="/calendar-white.svg" alt="calendar" width={22} height={22} />
                        <span>{loader ? <Skeleton variant='text' width={100} height={20} /> : (festivalDate ? formatFestivalDate(festivalDate) : '')}</span>
                    </div>
                }
            />
            <div className='mb-20'></div>
            <div className={`${BASE_CLASS}__festivals pt-[1rem] pb-[4rem] flex justify-center `}>
                <div className='festivals-container grid gap-[1.5rem] w-[95%] md:w-[90%]  2xl:w-[70%]  '>
                    <div className={`${BASE_CLASS}__view-container grid   gap-[1.5rem]   ${ubuntu.className}`}>
                        <div className={`${BASE_CLASS}__banner-container`}>
                            {
                                loader ? <Skeleton variant='text' width={'full'} height={'500px'}></Skeleton> :
                                    <Image style={{ width: "100%", height: '100%' }} className={`rounded-[8px]`} width={0} height={0} alt={`${selectedFestival.data.name} banner`} src={selectedFestival.data.ui_info.bg_image_v1 !== '' ? selectedFestival.data.ui_info.bg_image_v1 : selectedFestival.data.ui_info.bg_image} priority={true}></Image>
                            }
                        </div>
                        <div className={`${BASE_CLASS}__content-container grid grid-cols-3 gap-[1rem]`}>
                            <div className={`${BASE_CLASS}__description-container grid col-span-3 lg:col-span-2 gap-[1rem]`}>
                                {shownAnswer && (
                                    <div className={`${BASE_CLASS}__answer p-[1rem] bg-white flex flex-col gap-[0.75rem] rounded-[8px] border-[1px] border-[#E5E0DF] ${noto_sans.className}`}>
                                        <p className='text-[1rem] leading-[1.6] font-[500] text-[#1D4749]'>{shownAnswer.sentence}</p>
                                        {shownAnswer.rows.length > 0 && (
                                            <table className='w-full text-[14px] text-[#3C3838] border-collapse'>
                                                <tbody>
                                                    {shownAnswer.rows.map((row) => (
                                                        <tr key={row.label} className='border-t border-[#EDE4DD]'>
                                                            <th scope='row' className='py-2 pr-4 text-left font-[600] text-[#1D4749] whitespace-nowrap'>{row.label}</th>
                                                            <td className='py-2'>{row.value}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                        {shownAnswer.rows.length > 0 && cityNote && <p className='text-[14px] leading-[1.6] text-[#3C3838]'>{cityNote}</p>}
                                        {pageLinks.length > 0 && (
                                            <p className='text-[14px] text-[#8F8B8B]'>
                                                On this page:{' '}
                                                {pageLinks.map((link, index) => (
                                                    <Fragment key={link.href}>
                                                        {index > 0 && ' · '}
                                                        <a href={link.href} className='text-[#EE9322] font-[500] no-underline hover:underline'>{link.label}</a>
                                                    </Fragment>
                                                ))}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {shownAnswer && afterAnswer}
                                <div className={`${BASE_CLASS}__intro-container p-[1rem] bg-white flex flex-col gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]`}>
                                    <div className={`${BASE_CLASS}__intro-header text-[24px] pb-[0.5rem] font-[500] text-[#1D4749] ${ubuntu.className}`}>
                                        {loader ? <Skeleton variant='text' width={'full'} height={'full'}></Skeleton> : (selectedFestival.data.name)}
                                    </div>
                                    <div className={`${BASE_CLASS}__intro-description   ${noto_sans.className} font-[500] text-[1rem]  text-[#8F8B8B]`}>
                                        {loader ? <Skeleton variant='text' width={'full'} height={'full'}></Skeleton> : selectedFestival.data.description_1}
                                    </div>
                                    {!loader && festivalDate && !shownAnswer ? (
                                        <div className={`${BASE_CLASS}__date-line ${noto_sans.className} font-[600] text-[1rem] text-[#1D4749]`}>
                                            {selectedFestival.data.name} {festivalYear(festivalDate)} falls on {formatFestivalDate(festivalDate, true)}.
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex flex-col gap-4 lg:hidden">
                                    <div className={`${BASE_CLASS}__panchanga-container p-[1rem] bg-white lg:hidden flex  flex-col  gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]  `}>
                                        <div className={`${BASE_CLASS}__intro-header text-[1.25rem] pb-[0.5rem] font-[600] text-[#1D4749] ${ubuntu.className}`}>
                                            Panchanga
                                        </div>
                                        <div className={`${BASE_CLASS}__intro-description grid gap-[1.5rem] `}>
                                            {loader ? [1, 2,].map((n) => {
                                                return (
                                                    <div key={`festival-panchanga-tithi-skeleton-${n}`} className={`tithi__container grow grid`} >
                                                        <p className='tithi__name text-[#3C3838] font-bold text-[14px]'>
                                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                        </p>

                                                        <p className='tithi__start-end-time font-[500] text-[12px] text-[#8F8B8B] flex gap-[2px] whitespace-nowrap'>
                                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                        </p>

                                                    </div>
                                                )
                                            }) : ((selectedFestival.panchanga ?? todaysPanchanga)?.tithi ?? (festivalDate === answer?.date ? initialTithis : [])).map((tithi: Tithi, index: number) => {
                                                return (
                                                    <div key={tithi.key + Math.random()} className={`tithi__container grow grid`} >
                                                        <p className='tithi__name text-[#3C3838] font-bold text-[14px]'>{tithi.name}</p>

                                                        <div className='tithi__start-end-time font-[500] text-[12px] text-[#8F8B8B] flex gap-[2px] whitespace-nowrap'>
                                                            {`upto ${formatTimeIn(tithi.end_time, IST_TZ)} `}
                                                            {new Date(tithi.end_time * 1000).getDate() === new Date().getDate() ? '' : 'on '}
                                                            {((new Date(tithi.end_time * 1000).getDate() === new Date().getDate()) && (new Date(tithi.end_time * 1000).getMonth() + 1 === new Date().getMonth() + 1) && (new Date(tithi.end_time * 1000).getFullYear() === new Date().getFullYear())) ? 'today' : getFormattedDate(new Date(tithi.end_time * 1000), false, false, true, false, false)}
                                                        </div>

                                                    </div>
                                                )
                                            })}
                                            <div className={` border-[0.1rem] border-[orange] bg-[#EE9322] rounded-[0.75rem] flex place-content-center text-[white] p-2 px-5 flex gap-2 cursor-pointer`} onClick={() => handleDateChange(selectedFestival.date * 1000 || (new Date()).getTime())}>
                                                <div className={`font-[500] flex flex-wrap justify-center gap-[0.5rem] ${roboto.className} font-[500] `}>
                                                    <span className='text-center '>Panchanga For </span>
                                                    {loader ? <Skeleton variant='text' width={'50px'} height={'full'}></Skeleton> : Boolean(selectedFestival.date) ? formatFestivalDate(selectedFestival.date) : 'Today'}

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`${BASE_CLASS}__dates p-[1rem] bg-white flex flex-col  gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]`}>
                                        <div className={`${BASE_CLASS}__dates__header text-[1.25rem] pb-[0.5rem] font-[600] text-[#1D4749] ${ubuntu.className}`}>
                                            Upcoming Dates For {selectedFestival.data.name}
                                        </div>
                                        <div className='festival__upcoming-dates grid grid-cols-1 gap-[1rem] h-[100px]'>
                                            {loader ? [1, 2, 3].map((date) => {
                                                return (
                                                    <div key={date + Math.random()} className='flex gap-[12px] items-center cursor-pointer' onClick={() => handleDateChange(date * 1000)}>
                                                        <div className={`h-full w-[0.5rem] rounded-[1rem] bg-[#E9B824]`}
                                                        >

                                                        </div>
                                                        <div className={`tithi__container grow grid`} >
                                                            <p className={`tithi__name text-[#3C3838]  text-[16px] ${noto_sans.className} font-[700] `}>
                                                                <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>

                                                            </p>

                                                            <p className='tithi__endtime font-[500] text-[12px] text-[#8F8B8B]'>
                                                                <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                            </p>

                                                        </div>
                                                    </div>
                                                )
                                            }) :

                                                selectedFestival.data.upcoming_dates?.length ?
                                                    selectedFestival.data.upcoming_dates?.map((date) => {
                                                        if (new Date(date * 1000).getTime() > new Date().getTime())
                                                            return (
                                                                <div key={date} onClick={() => handleDateChange(date * 1000)} className="cursor-pointer px-1 py-1 flex-shrink-0 flex flex-row justify-between gap-6">
                                                                    <div className="flex flex-row gap-2">
                                                                        {/* Date */}
                                                                        <div
                                                                            className={`text-[14px] font-[700] text-[#3C3838] ${noto_sans.className}`}
                                                                        >
                                                                            {formatFestivalDate(date)}
                                                                        </div>
                                                                        {/* Day */}
                                                                        <div className="text-[14px] font-[500] text-[#8F8B8B]">
                                                                            {festivalWeekday(date)}
                                                                        </div>
                                                                    </div>
                                                                    <Image src="/arrow-orange.svg" alt="arrow right" width={20} height={20} className="mb-1" />
                                                                </div>
                                                            )
                                                    })
                                                    : <div className={`text-[#1D4749] font-bold ${noto_sans} text-[1rem] whitespace-nowrap`}>
                                                        No upcoming dates for this {selectedFestival.data.type}
                                                    </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className={`${BASE_CLASS}__about-container p-[1rem] bg-white flex flex-col gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]`}>
                                    <div className={`${BASE_CLASS}__about-header text-[24px] pb-[0.5rem] font-[500] text-[#1D4749] ${ubuntu.className}`}>
                                        About
                                    </div>
                                    <div className={`${BASE_CLASS}__about-description  ${noto_sans.className} font-[500] text-[1rem] text-[#8F8B8B]`}>
                                        {loader ? <>

                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                        </> : selectedFestival.data.description_2}
                                    </div>
                                </div>
                                {content}
                            </div>
                            <div className="grid gap-4">
                                <div className={`${BASE_CLASS}__ -container p-[1rem] bg-white hidden  lg:flex  flex-col  gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]  `}>
                                    <div className={`${BASE_CLASS}__intro-header text-[24px] pb-[0.5rem] font-[500] text-[#1D4749] ${ubuntu.className}`}>
                                        Panchanga
                                    </div>
                                    <div className={`${BASE_CLASS}__intro-description grid gap-[1.5rem] `}>
                                        {loader ? [1, 2,].map((n) => {
                                            return (
                                                <div key={`festival-panchanga-tithi-skeleton-${n}`} className={`tithi__container grow grid`} >
                                                    <p className='tithi__name text-[#3C3838] font-bold text-[14px]'>
                                                        <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                    </p>

                                                    <p className='tithi__start-end-time font-[500] text-[12px] text-[#8F8B8B] flex gap-[2px] whitespace-nowrap'>
                                                        <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                    </p>

                                                </div>
                                            )
                                        }) : ((selectedFestival.panchanga ?? todaysPanchanga)?.tithi ?? (festivalDate === answer?.date ? initialTithis : [])).map((tithi: Tithi, index: number) => {
                                            return (
                                                <div key={tithi.key + Math.random()} className={`tithi__container grow grid`} >
                                                    <p className='tithi__name text-[#3C3838] font-bold text-[18px]'>{tithi.name}</p>

                                                    <div className={`${noto_sans.className} tithi__start-end-time font-[500] text-[16px] text-[#8F8B8B] flex gap-[2px] whitespace-nowrap`}>
                                                        {`upto ${formatTimeIn(tithi.end_time, IST_TZ)} `}
                                                        {new Date(tithi.end_time * 1000).getDate() === new Date().getDate() ? '' : 'on  '}
                                                        {((new Date(tithi.end_time * 1000).getDate() === new Date().getDate()) && (new Date(tithi.end_time * 1000).getMonth() + 1 === new Date().getMonth() + 1) && (new Date(tithi.end_time * 1000).getFullYear() === new Date().getFullYear())) ? 'today' : getFormattedDate(new Date(tithi.end_time * 1000), false, false, true, false, false)}
                                                    </div>

                                                </div>
                                            )
                                        })}
                                        <div className={`border-[0.1rem] border-[orange] bg-[#EE9322] rounded-[0.75rem] flex place-content-center text-[white] p-2 px-5 flex gap-2 cursor-pointer`} onClick={() => handleDateChange(selectedFestival.date * 1000 || (new Date()).getTime())}>
                                            <div className={`font-[500] flex flex-wrap justify-center gap-[0.5rem] ${roboto.className} font-[500]`}>
                                                <span className='text-center '>PANCHANGA FOR </span>
                                                {loader ? <Skeleton variant='text' width={'50px'} height={'full'}></Skeleton> : Boolean(selectedFestival.date) ? formatFestivalDate(selectedFestival.date) : 'TODAY'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={`${BASE_CLASS}__dates p-[1rem] bg-white hidden max-w-[400px] lg:flex lg:flex-col gap-[1rem] rounded-[8px] border-[1px] border-[#E5E0DF]`}>
                                    <div className={`${BASE_CLASS}__dates__header text-[24px] pb-[0.5rem] font-[500] text-[#1D4749] ${ubuntu.className}`}>
                                        Upcoming Dates For {selectedFestival.data.name}
                                    </div>
                                    <div className='festival__upcoming-dates grid grid-cols-1 gap-[0.75rem] overflow-scroll max-w-[400px]'>
                                        {loader ? [1, 2, 3].map((date) => {
                                            return (
                                                <div key={date + Math.random()} className='flex gap-[12px] items-center cursor-pointer' onClick={() => handleDateChange(date * 1000)}>
                                                    <div className={`h-full w-[0.5rem] rounded-[1rem] bg-[#E9B824]`}
                                                    >

                                                    </div>
                                                    <div className={`tithi__container grow grid`} >
                                                        <p className={`tithi__name text-[#3C3838]  text-[16px] ${noto_sans.className} font-[700] `}>
                                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>

                                                        </p>

                                                        <p className='tithi__endtime font-[500] text-[12px] text-[#8F8B8B]'>
                                                            <Skeleton variant='text' width={'full'} height={'full'}></Skeleton>
                                                        </p>

                                                    </div>
                                                </div>
                                            )
                                        }) :

                                            selectedFestival.data.upcoming_dates?.length ?
                                                selectedFestival.data.upcoming_dates?.map((date) => {
                                                    if (new Date(date * 1000).getTime() > new Date().getTime())
                                                        return (
                                                            <div key={date} onClick={() => handleDateChange(date * 1000)} className="cursor-pointer px-1 py-1 flex-shrink-0 flex flex-row justify-between gap-6">
                                                                <div className="flex flex-row gap-2">
                                                                    {/* Date */}
                                                                    <div
                                                                        className={`text-[14px] font-[700] text-[#3C3838] ${noto_sans.className}`}
                                                                    >
                                                                        {formatFestivalDate(date)}
                                                                    </div>
                                                                    {/* Day */}
                                                                    <div className="text-[14px] font-[500] text-[#8F8B8B]">
                                                                        {festivalWeekday(date)}
                                                                    </div>
                                                                </div>
                                                                <Image src="/arrow-orange.svg" alt="arrow right" width={20} height={20} className="mb-1" />
                                                            </div>
                                                        )
                                                })
                                                : <div className={`text-[#1D4749] font-bold ${noto_sans} text-[1rem] whitespace-nowrap`}>
                                                    No upcoming dates for this {selectedFestival.data.type}
                                                </div>
                                        }
                                    </div>
                                </div>
                            </div>


                        </div>

                    </div>
                    {selectedFestival.data.prarthanas && Boolean(selectedFestival.data.prarthanas.length) && <div id="festival-prarthanas" className="scroll-mt-24"><PrarthanaForFestival prarthanas={selectedFestival.data.prarthanas} festivalName={selectedFestival.data.name || selectedFestival.data.observance_name} /></div>}

                    {/* SEO T2.5: crawlable festival → prarthana conversion link. Points to
                        the prarthana-for-festivals hub (a real, indexable prarthana collection);
                        the festival's own prarthanas are rendered inline above. */}
                    <Link
                        href="/prarthana/festival"
                        className={`${ubuntu.className} flex items-center justify-between gap-3 bg-white rounded-[8px] border-[1px] border-[#E5E0DF] px-5 py-4 text-[#1D4749] no-underline hover:bg-[#FFEEBB] transition-colors`}
                    >
                        <span className="font-[600]">
                            Prarthanas &amp; mantras for {festivalName || 'this festival'}
                            <span className="block text-[13px] font-[400] text-[#8F8B8B]">
                                Listen to devotional prarthanas and chants for Hindu festivals
                            </span>
                        </span>
                        <span aria-hidden="true" className="text-[#EE9322] text-[20px]">→</span>
                    </Link>

                    {/* SEO: App-redirect CTA (Pandit booking, prarthanas, panchanga, astrology) */}
                    <div id="festival-app-cta">
                        <FestivalAppCTA festivalName={festivalName} heading={ctaHeading} />
                    </div>

                    {/* SEO: Festival FAQ */}
                    {beforeFaq}
                    {faqs.length > 0 && <div id="festival-faq" className="scroll-mt-24"><FestivalFAQ faqs={faqs} /></div>}
                </div>


            </div>
        </section>

        {/* Trusted by Hindus worldwide — testimonials
        <section className={`w-full bg-[#FDF8E9] py-10 ${ubuntu.className}`}>
            <div className="w-[95%] md:w-[90%] 2xl:w-[70%] mx-auto">
                <h2 className="text-[26px] md:text-[32px] font-[600] text-[#1D4749] text-center mb-2">
                    Trusted by Hindus worldwide
                </h2>
                <p className={`${noto_sans.className} text-[14px] md:text-[16px] text-[#3C3838] text-center mb-6`}>
                    Thousands rely on Dharmayana for festival poojas, prarthanas, accurate Panchanga and daily rituals.
                </p>
            </div>
            <InfiniteScrollTestimonials testimonials={TESTIMONIALS} />
        </section>*/}

        {/* Sticky "Get the App" nudge — mobile compact, desktop full */}
        {showStickyNudge && (
            <div className="fixed bottom-0 left-0 right-0 z-50">
                <div className="mx-[12px] sm:mx-[16px] lg:mx-[16px] rounded-[12px] mb-[8px] bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/sticky-nudge-bg.png')" }}>
                    {/* Mobile compact nudge */}
                    <div className="sm:hidden px-[12px] py-[10px] flex items-center justify-between">
                        <p className={`${ubuntu.className} text-[16px] text-white leading-tight`}>
                            Book Pandit & Festival Pooja<br />on the Dharmayana App
                        </p>
                        <button
                            onClick={() => document.getElementById('prarthana-homepage-banner')?.scrollIntoView({ behavior: 'smooth' })}
                            className={`${ubuntu.className} shrink-0 bg-[#FFFFFF] text-[#EE9322] text-[12px] font-[600] px-[14px] py-[6px] rounded-[6px]`}
                        >
                            GET THE APP
                        </button>
                    </div>
                    {/* Desktop full nudge */}
                    <div className="hidden sm:flex items-center justify-between px-[16px] lg:px-[48px] py-[12px] max-w-[1400px] mx-auto">
                        <div>
                            <h3 className={`${ubuntu.className} font-[500] text-[18px] lg:text-[20px] text-white`}>
                                Book {festivalName || 'Festival'} Pooja on the Dharmayana App
                            </h3>
                            <p className={`${ubuntu.className} text-[13px] lg:text-[14px] text-[#F7F3F2] mt-[2px]`}>
                                Verified Pandits, prarthanas with audio and accurate Panchanga, all in one place.
                            </p>
                        </div>
                        <div className="flex gap-[10px]">
                            <a href={process.env.NEXT_PUBLIC_KOCHAVA_LINK_ANDROID || 'https://play.google.com/store/apps/details?id=in.dharmayana.android'} target="_blank">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/Play Store Badge.webp`}
                                    alt="Get it on Google Play"
                                    width={135}
                                    height={40}
                                    className="h-[40px] w-auto hover:scale-105 transition duration-300"
                                />
                            </a>
                            <a href={process.env.NEXT_PUBLIC_KOCHAVA_LINK_IOS || 'https://apps.apple.com/in/app/dharmayana-hindu-app/id6474740467'} target="_blank">
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/iOS Badge.webp`}
                                    alt="Download on the App Store"
                                    width={135}
                                    height={40}
                                    className="h-[40px] w-auto hover:scale-105 transition duration-300"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>

    )
} 